import { Injectable, Injector } from '@angular/core';
import { concatMap, from, map, merge, Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, retry, shareReplay, switchMap, tap } from 'rxjs/operators';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { AuthenticationService } from '@services/authentication.service';

@Injectable({
	providedIn: 'root'
})
export abstract class HubSdkBase {
	private authenticationService: AuthenticationService;
	private isTerminating = false;
	private connection$: Observable<HubConnection>;
	private connectionRef: HubConnection;
	protected constructor(private injector: Injector, private hubName: string) {
		this.authenticationService = this.injector.get(AuthenticationService);
		this.connection$ = this.createConnection().pipe(
			tap({
				next: () => console.log(`%cConnection successful!::${hubName}`, 'color: green'),
				unsubscribe: () => this.terminateConnection().subscribe(),
			}),
			catchError((error) => {
				console.warn(error?.message);
				return throwError(() => error);
			}),
			retry({delay: Math.floor(Math.random() * (10 - 5 + 1) + 5) * 1000}),
			shareReplay({bufferSize: 1, refCount: true}),
		);
	}

	private createConnection(): Observable<HubConnection> {
		return this.authenticationService?.getToken$().pipe(
			concatMap((token) => {
				if (this.connectionRef) {
					this.isTerminating = true;
					return this.terminateConnection().pipe(
						map(() => token)
					);
				}
				return of(token)
			}),
			switchMap((token) => {
				const connection = new HubConnectionBuilder()
					.withUrl(`/signalr/${this.hubName}`, {accessTokenFactory: () => token})
					.configureLogging(LogLevel.Error)
					.build();

				this.connectionRef = connection;

				return new Observable<HubConnection>((observer) => {
					connection.start()
						.then(() => {
							observer.next(connection);
							connection.onclose(() => {
								if (!this.isTerminating) {
									observer.error(new Error("Connection closed unexpectedly. Reconnecting..."));
								} else {
									observer.complete();
									observer.unsubscribe();
								}
							});
						})
						.catch((error) => {
							console.warn("There was an issue creating the connection. Reconnecting...");
							observer.error(error);
						});
				});
			})
		);
	}

	protected on$<TResponse>(methodName: string): Observable<TResponse> {
		return this.connection$.pipe(
			mergeMap((connection) =>
				merge(
					this.wrapInvoke<TResponse>(connection, 'GetLatestFrom', methodName),
					this.wrapOn<TResponse>(connection, methodName)
				)
			)
		);
	}

	protected listen$<TResponse>(methodName: string): Observable<TResponse> {
		return this.connection$.pipe(
			mergeMap((connection) => this.wrapOn<TResponse>(connection, methodName))
		);
	}

	protected invoke$<T>(methodName: string, params: any = null): Observable<T> {
		return this.connection$.pipe(
			mergeMap((connection) => this.wrapInvoke<T>(connection, methodName, params))
		);
	}

	private wrapInvoke<T>(connection: HubConnection, methodName: string, params: any = null): Observable<T> {
		return from(params ? connection.invoke(methodName, params) : connection.invoke(methodName));
	}

	private wrapOn<T>(connection: HubConnection, methodName: string): Observable<T> {
		return new Observable<T>((subscriber) => {
			const handler = (response: T) => {
				subscriber.next(response);
			}
			connection.on(methodName, handler);
			return () => {
				connection.off(methodName, handler);
			}
		});
	}

	private terminateConnection() {
		return from(this.connectionRef.stop()).pipe(
			tap({
				next: () => this.isTerminating = false,
				error: () => this.isTerminating = false
			})
		);
	}
}
